import * as THREE from 'three';
import type { World } from '../../shared/types.js';
import { Heightfield } from '../../shared/heightfield.js';

/**
 * The camera after the chequered flag.
 *
 * Racing games end badly when the picture stops: the car freezes, a table drops over the
 * top of it, and the city you spent two minutes driving through is gone. So instead the
 * camera lifts off the car and flies a slow circle over whatever is still interesting —
 * the cars still coming in, or the finish line itself — while the results sit off to one
 * side of it.
 *
 * Nothing here reads the race; the game hands it a subject each frame and this decides
 * where to be. Subjects change (a car finishes, the next one takes over) so both the
 * focus and the camera are critically damped towards their targets rather than snapped:
 * a cut is fine, a jump cut every time someone crosses the line is not.
 */

/** Degrees a second. Slow enough to read the city, fast enough not to look broken. */
const ORBIT_SPEED = 11;
/** Metres. A radius that frames a car and the road it is on. */
const ORBIT_RADIUS = 46;
const ORBIT_HEIGHT = 24;
/**
 * Metres of air the camera keeps above whatever it is flying over.
 *
 * Without this the first version of the shot spent most of its circle inside a fifth-floor
 * flat: 24 m is over the roofline in a village and well under it in Kraków's Old Town,
 * never mind Midtown Manhattan.
 */
const ROOF_CLEARANCE = 14;
/**
 * Radius grows with height so the camera keeps looking *across* the city at about 35°
 * rather than straight down at the roofs. Purely a framing choice.
 */
const RADIUS_PER_HEIGHT = 1.4;
/** The orbit breathes in and out a little so it never reads as a turntable. */
const BREATHE_AMPLITUDE = 9;
const BREATHE_PERIOD = 13;
/** Look slightly above the ground, or the subject sits on the bottom edge of the frame. */
const LOOK_HEIGHT = 2.4;

/** Per-second convergence. Higher is snappier; these are tuned for "expensive drone". */
const FOCUS_LERP = 1.4;
const POSITION_LERP = 2.2;

const OUTRO_FOV = 48;

export interface OutroSubject {
  x: number;
  z: number;
  /** Shown in the HUD caption, e.g. "Watching Ada". Null for the finish line. */
  label: string | null;
}

/**
 * The tallest thing in each patch of the city, on a coarse grid.
 *
 * Built once per world from the building list, which the client already has in full. It
 * is deliberately not the collision index: that one drops buildings a road passes
 * through, and a camera does not care whether a viaduct is drivable — it will still be
 * inside it. Cells are 32 m because the shot only needs to know roughly how high the
 * neighbourhood is, and a query reads the 3×3 around it so the camera clears a tower it
 * is about to fly past rather than the one it is already in.
 */
export class Skyline {
  private readonly cells = new Map<number, number>();
  private readonly cell = 32;

  constructor(world: World) {
    const ground = world.ground ? Heightfield.from(world.ground) : Heightfield.flat();
    for (const building of world.buildings) {
      if (!(building.height > 0)) continue;
      // Stamp the footprint's bounding box. Exact coverage would be tidier and makes no
      // visible difference to a camera that is 14 m above the answer.
      let minX = Infinity;
      let maxX = -Infinity;
      let minZ = Infinity;
      let maxZ = -Infinity;
      for (let i = 0; i < building.poly.length; i += 2) {
        minX = Math.min(minX, building.poly[i]);
        maxX = Math.max(maxX, building.poly[i]);
        minZ = Math.min(minZ, building.poly[i + 1]);
        maxZ = Math.max(maxZ, building.poly[i + 1]);
      }
      if (!Number.isFinite(minX)) continue;
      // A roof's height above the ground plane is its own height plus the hill it stands
      // on. Ignoring the terrain would have the orbit clear a 20 m block correctly at the
      // bottom of a valley and fly straight through the identical one on the ridge.
      const top = building.height + ground.heightAt((minX + maxX) / 2, (minZ + maxZ) / 2);
      for (let cx = Math.floor(minX / this.cell); cx <= Math.floor(maxX / this.cell); cx++) {
        for (let cz = Math.floor(minZ / this.cell); cz <= Math.floor(maxZ / this.cell); cz++) {
          const key = cx * 100003 + cz;
          if (top > (this.cells.get(key) ?? 0)) this.cells.set(key, top);
        }
      }
    }
  }

  /** Tallest roof within one cell of (x, z). */
  heightAt(x: number, z: number): number {
    const cx = Math.floor(x / this.cell);
    const cz = Math.floor(z / this.cell);
    let top = 0;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const h = this.cells.get((cx + dx) * 100003 + (cz + dz));
        if (h !== undefined && h > top) top = h;
      }
    }
    return top;
  }
}

export class OutroCamera {
  private angle = 0;
  private elapsed = 0;
  private readonly focus = new THREE.Vector3();
  private readonly position = new THREE.Vector3();
  private readonly desired = new THREE.Vector3();
  private started = false;
  private skyline: Skyline | null = null;

  /** Handed the world whenever one loads, so the orbit knows how tall the city is. */
  setSkyline(skyline: Skyline | null): void {
    this.skyline = skyline;
  }

  /** Begins the fly-out from wherever the camera currently is, so there is no cut in. */
  reset(camera: THREE.PerspectiveCamera, subject: OutroSubject): void {
    this.elapsed = 0;
    this.started = true;
    this.focus.set(subject.x, LOOK_HEIGHT, subject.z);
    this.position.copy(camera.position);
    // Start the orbit behind the camera's current bearing so the first movement
    // continues the shot rather than reversing it.
    this.angle = Math.atan2(camera.position.x - subject.x, camera.position.z - subject.z);
  }

  update(camera: THREE.PerspectiveCamera, subject: OutroSubject, dt: number): void {
    if (!this.started) this.reset(camera, subject);
    this.elapsed += dt;
    this.angle += THREE.MathUtils.degToRad(ORBIT_SPEED) * dt;

    // `1 - exp(-k·dt)` rather than `k·dt`: the same easing at any frame rate, and it
    // cannot overshoot when a frame runs long.
    const focusK = 1 - Math.exp(-FOCUS_LERP * dt);
    this.focus.x += (subject.x - this.focus.x) * focusK;
    this.focus.z += (subject.z - this.focus.z) * focusK;
    this.focus.y = LOOK_HEIGHT;

    const breathe = Math.sin((this.elapsed / BREATHE_PERIOD) * Math.PI * 2);

    // Height first, because the radius follows from it. The roofline is sampled both
    // under the subject and under where the camera is heading — clearing only one of the
    // two puts the lens through a wall on the way past.
    const overSubject = this.skyline?.heightAt(this.focus.x, this.focus.z) ?? 0;
    const provisional = this.focus.x + Math.sin(this.angle) * ORBIT_RADIUS;
    const provisionalZ = this.focus.z + Math.cos(this.angle) * ORBIT_RADIUS;
    const overCamera = this.skyline?.heightAt(provisional, provisionalZ) ?? 0;
    const height =
      Math.max(ORBIT_HEIGHT, Math.max(overSubject, overCamera) + ROOF_CLEARANCE) +
      BREATHE_AMPLITUDE * 0.35 * Math.cos((this.elapsed / BREATHE_PERIOD) * Math.PI * 2);

    const radius = Math.max(ORBIT_RADIUS, height * RADIUS_PER_HEIGHT) + BREATHE_AMPLITUDE * breathe;

    this.desired.set(
      this.focus.x + Math.sin(this.angle) * radius,
      height,
      this.focus.z + Math.cos(this.angle) * radius,
    );

    const posK = 1 - Math.exp(-POSITION_LERP * dt);
    this.position.lerp(this.desired, posK);

    camera.position.copy(this.position);
    camera.lookAt(this.focus);
    if (camera.fov !== OUTRO_FOV) {
      camera.fov = OUTRO_FOV;
      camera.updateProjectionMatrix();
    }
  }

  stop(): void {
    this.started = false;
  }
}
